import { useEffect, useState, type JSX, type ReactNode } from "react";

import { useMyCompaniesQuery } from "../../lib/api/users.js";
import { COMPANY_CHOSEN_KEY } from "../../lib/company-session.js";
import { LoadingState } from "../data/LoadingState.js";

interface CompanyGateProps {
  children: ReactNode;
}

function readChosenFlag(): boolean {
  try {
    return localStorage.getItem(COMPANY_CHOSEN_KEY) === "1";
  } catch {
    return false;
  }
}

function setChosenFlag(): void {
  try {
    localStorage.setItem(COMPANY_CHOSEN_KEY, "1");
  } catch {
    /* ignore disabled storage */
  }
}

/**
 * Post-login company gate. Wraps the authenticated AppShell subtree and
 * ensures the user has an active company before letting them in.
 *
 * - 0 companies  → redirect to /onboarding (create one).
 * - >1 companies, not yet chosen → redirect to /select-company.
 * - exactly 1, or already chosen → render children (marking single-company
 *   users as chosen for coherence).
 *
 * /onboarding and /select-company are standalone routes NOT wrapped by this
 * gate — they are the escape hatches.
 */
export function CompanyGate({ children }: CompanyGateProps): JSX.Element {
  const companiesQuery = useMyCompaniesQuery();
  const companies = companiesQuery.data?.items ?? [];
  const loading = companiesQuery.isLoading;
  // Only trust the count when the query actually SUCCEEDED. On error,
  // `data` is undefined and `companies` would be `[]` — without this guard a
  // transient failure (network blip, a 401 that didn't refresh in time) would
  // be indistinguishable from "0 companies" and wrongly bounce a user who has
  // access to /onboarding.
  const loaded = companiesQuery.isSuccess;
  const single = loaded && companies.length === 1;

  // A freshly-accepted invite can make the companies list read empty for a
  // moment (membership just committed; this query may have been in flight with
  // the old cookie, or there's brief read lag). Rather than bounce straight to
  // /onboarding on an empty list, retry a few times first — only conclude
  // "truly no company" after the list is still empty across retries.
  const MAX_EMPTY_RETRIES = 4;
  const [emptyAttempts, setEmptyAttempts] = useState(0);
  const emptyAfterLoad = loaded && companies.length === 0;
  const retryingEmpty = emptyAfterLoad && emptyAttempts < MAX_EMPTY_RETRIES;

  useEffect(() => {
    if (!retryingEmpty) return;
    const t = setTimeout(() => {
      setEmptyAttempts((n) => n + 1);
      void companiesQuery.refetch();
    }, 700);
    return () => clearTimeout(t);
  }, [retryingEmpty, companiesQuery]);

  // The gate's escape hatches (/onboarding, /select-company) are root pages on
  // the public router. Redirect via FULL navigation (window.location) so we
  // leave the app router's `/t/<code>` basename — a react-router <Navigate>
  // would resolve them under the basename and 404.
  let redirectTo: string | null = null;
  if (loaded) {
    // Only send to onboarding once retries are exhausted and it's still empty.
    if (companies.length === 0 && emptyAttempts >= MAX_EMPTY_RETRIES) redirectTo = "/onboarding";
    else if (companies.length > 1 && !readChosenFlag()) redirectTo = "/select-company";
  }

  // Keep the chosen flag coherent for single-company users.
  useEffect(() => {
    if (single) setChosenFlag();
  }, [single]);

  useEffect(() => {
    if (redirectTo) window.location.replace(redirectTo);
  }, [redirectTo]);

  if (loading || redirectTo || retryingEmpty) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingState variant="spinner" message="Loading companies…" />
      </div>
    );
  }

  // Query failed (not loading, not success): show a retry rather than falling
  // through — and crucially NOT /onboarding, which would strand a user who
  // actually has access.
  if (companiesQuery.isError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm text-muted-foreground">Couldn’t load your companies.</p>
        <button
          type="button"
          onClick={() => void companiesQuery.refetch()}
          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent"
        >
          Retry
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
